import { Marker } from 'react-map-gl/maplibre';
import { Flame, Plus, Hammer, Shield, Gavel } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import type { Station, StationKind } from '@/state/types';

interface MarkerStyle {
  bg: string;
  border: string;
  size: 'sm' | 'lg';
  icon: LucideIcon;
  iconLabel: string;
}

const STYLES: Record<StationKind, MarkerStyle> = {
  'bf-station': {
    bg: '#b91c1c', // darker red — Hauptwache
    border: '#ffffff',
    size: 'sm',
    icon: Flame,
    iconLabel: 'Berufsfeuerwehr',
  },
  'ff-station': {
    bg: '#dc2626',
    border: '#ffffff',
    size: 'sm',
    icon: Flame,
    iconLabel: 'Freiwillige Feuerwehr',
  },
  'ems-station': {
    bg: '#f97316',
    border: '#ffffff',
    size: 'sm',
    icon: Plus,
    iconLabel: 'Rettungswache',
  },
  hospital: {
    bg: '#374151',
    border: '#ffffff',
    size: 'lg',
    icon: Plus,
    iconLabel: 'Krankenhaus',
  },
  // Phase 10.1 — Polizei / Gericht
  'police-station': {
    bg: '#1d4ed8',
    border: '#ffffff',
    size: 'sm',
    icon: Shield,
    iconLabel: 'Polizeirevier',
  },
  'riot-police-station': {
    bg: '#1e40af',
    border: '#ffffff',
    size: 'sm',
    icon: Shield,
    iconLabel: 'Bereitschaftspolizei',
  },
  'sek-base': {
    bg: '#312e81',
    border: '#ffffff',
    size: 'sm',
    icon: Shield,
    iconLabel: 'SEK-Basis',
  },
  'federal-police-station': {
    bg: '#0c4a6e',
    border: '#ffffff',
    size: 'sm',
    icon: Shield,
    iconLabel: 'Bundespolizei',
  },
  courthouse: {
    bg: '#475569',
    border: '#ffffff',
    size: 'lg',
    icon: Gavel,
    iconLabel: 'Gericht',
  },
};

export function StationMarkers() {
  const stations = useGameStore((s) => s.stations);
  const visible = useGameStore((s) => s.uiPrefs.layerToggles.stations);
  const gameTime = useGameStore((s) => s.gameTime);
  const selectDetail = useGameStore((s) => s.selectDetail);

  if (!visible) return null;

  return (
    <>
      {stations.map((s) => {
        const style = STYLES[s.kind];
        const Icon = style.icon;
        const sizeClass = style.size === 'lg' ? 'h-9 w-9' : 'h-8 w-8';
        const iconClass = style.size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
        const construction = constructionState(s, gameTime);

        return (
          <Marker
            key={s.id}
            longitude={s.position.lng}
            latitude={s.position.lat}
            anchor="bottom"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                selectDetail({ kind: 'station', id: s.id });
              }}
              title={
                construction
                  ? `${style.iconLabel} – ${s.name} (${Math.round(construction.progress * 100)} % gebaut)`
                  : `${style.iconLabel} – ${s.name}`
              }
              aria-label={`${style.iconLabel} – ${s.name}`}
              className="relative flex flex-col items-center"
            >
              <span
                className={[
                  'flex items-center justify-center rounded-lg shadow-lg transition hover:scale-110',
                  sizeClass,
                  construction ? 'opacity-50 grayscale' : '',
                ].join(' ')}
                style={{
                  background: style.bg,
                  border: `2px solid ${style.border}`,
                }}
              >
                {construction ? (
                  <Hammer className={[iconClass, 'text-white'].join(' ')} aria-hidden />
                ) : (
                  <Icon className={[iconClass, 'text-white'].join(' ')} aria-hidden />
                )}
              </span>
              {construction && (
                <span
                  className="mt-0.5 h-1 w-10 overflow-hidden rounded-full bg-bg-primary/80"
                  aria-hidden
                >
                  <span
                    className="block h-full bg-amber-400 transition-all"
                    style={{ width: `${Math.round(construction.progress * 100)}%` }}
                  />
                </span>
              )}
            </button>
          </Marker>
        );
      })}
    </>
  );
}

function constructionState(s: Station, now: number): { progress: number } | null {
  if (!s.buildStartedAt || !s.buildCompleteAt) return null;
  if (now >= s.buildCompleteAt) return null;
  const total = s.buildCompleteAt - s.buildStartedAt;
  if (total <= 0) return null;
  const elapsed = now - s.buildStartedAt;
  return { progress: Math.max(0, Math.min(1, elapsed / total)) };
}
